/**
 * Martina London — site header behavior.
 * Vanilla JS, no dependencies. Loaded with `defer`.
 */
(function () {
  'use strict';

  const header = document.querySelector('[data-site-header]');
  if (!header) return;

  /* ----------------------------------------------------------
   * Scroll state — main bar swaps from transparent to solid
   * once the user has scrolled past 30px. Lower threshold ensures
   * the header is visibly solid almost immediately on mobile so it
   * never blends into light content behind it.
   * -------------------------------------------------------- */
  const BAR_THRESHOLD = 30;
  let barScrolled = false;

  const syncScrollState = () => {
    const next = window.scrollY > BAR_THRESHOLD;
    if (next === barScrolled) return;
    barScrolled = next;
    header.classList.toggle('site-header--scrolled', next);
  };

  window.addEventListener('scroll', syncScrollState, { passive: true });
  syncScrollState();

  /* ----------------------------------------------------------
   * Country selector dropdown
   *   - Toggle click toggles open/close.
   *   - Click outside closes.
   *   - Click on a row inside submits the localization form.
   *   - Escape closes (handled globally below).
   * -------------------------------------------------------- */
  const countryToggle = header.querySelector('[data-country-toggle]');
  const countryDropdown = header.querySelector('[data-country-dropdown]');

  const closeCountry = () => {
    if (!countryDropdown) return;
    countryDropdown.classList.remove('is-open');
    if (countryToggle) countryToggle.setAttribute('aria-expanded', 'false');
  };

  if (countryToggle && countryDropdown) {
    countryToggle.addEventListener('click', (e) => {
      e.preventDefault();
      const open = countryDropdown.classList.toggle('is-open');
      countryToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    document.addEventListener('click', (e) => {
      if (countryToggle.contains(e.target)) return;
      if (countryDropdown.contains(e.target)) return;
      closeCountry();
    });

    // Click on a country option: update the hidden input and submit.
    countryDropdown.addEventListener('click', (e) => {
      const option = e.target.closest('[data-country-option]');
      if (option) {
        const form = document.getElementById('localization-form-desktop');
        const input = form && form.querySelector('[data-localization-country]');
        if (input) {
          input.value = option.dataset.countryOption;
          form.submit();
        }
      }
      closeCountry();
    });
  }

  // Mobile drawer: auto-submit localization form on select change.
  const mobileLocalizationSelect = document.querySelector('#localization-form-mobile select');
  if (mobileLocalizationSelect) {
    mobileLocalizationSelect.addEventListener('change', () => {
      mobileLocalizationSelect.closest('form').submit();
    });
  }

  // Country flag emoji from 2-letter ISO code (works for GB, US, EU, AU, JP, etc.)
  const isoToFlag = (iso) => {
    if (!iso || iso.length !== 2) return '';
    try {
      return String.fromCodePoint(
        ...iso.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0))
      );
    } catch (e) {
      return '';
    }
  };

  // Prepend flag to dropdown option buttons only.
  // [data-country-option] scopes to options only — the toggle also has [data-country-iso]
  // but NOT [data-country-option], so it is not caught here (prevents double flags).
  document.querySelectorAll('[data-country-option][data-country-iso]').forEach((el) => {
    const flag = isoToFlag(el.dataset.countryIso);
    if (flag) el.insertAdjacentText('afterbegin', flag + ' ');
  });

  // Prepend flag to the toggle label span only — separate from the loop above.
  if (countryToggle) {
    const label = countryToggle.querySelector('[data-country-label]');
    const flag = isoToFlag(countryToggle.dataset.countryIso);
    if (label && flag) label.textContent = flag + ' ' + label.textContent;
  }

  /* ----------------------------------------------------------
   * Search overlay
   * -------------------------------------------------------- */
  const searchOverlay = document.querySelector('[data-search-overlay]');
  const searchInput = searchOverlay
    ? searchOverlay.querySelector('input[type="search"]')
    : null;

  const lockScroll = () => {
    document.body.style.overflow = 'hidden';
  };
  const unlockScroll = () => {
    document.body.style.overflow = '';
  };

  const openSearch = () => {
    if (!searchOverlay) return;
    searchOverlay.classList.add('is-open');
    searchOverlay.setAttribute('aria-hidden', 'false');
    lockScroll();
    setTimeout(() => {
      if (searchInput) searchInput.focus();
    }, 60);
  };

  const closeSearch = () => {
    if (!searchOverlay) return;
    searchOverlay.classList.remove('is-open');
    searchOverlay.setAttribute('aria-hidden', 'true');
    unlockScroll();
  };

  document.querySelectorAll('[data-search-open]').forEach((btn) => {
    btn.addEventListener('click', openSearch);
  });
  document.querySelectorAll('[data-search-close]').forEach((btn) => {
    btn.addEventListener('click', closeSearch);
  });

  // Focus trap inside search overlay.
  if (searchOverlay) {
    searchOverlay.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const focusable = searchOverlay.querySelectorAll(
        'input, button, a[href]'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ----------------------------------------------------------
   * Mobile drawer
   * -------------------------------------------------------- */
  const drawer = document.querySelector('[data-mobile-drawer]');

  const openDrawer = () => {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll();
  };

  const closeDrawer = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();
  };

  document.querySelectorAll('[data-drawer-open]').forEach((btn) => {
    btn.addEventListener('click', openDrawer);
  });
  document.querySelectorAll('[data-drawer-close]').forEach((btn) => {
    btn.addEventListener('click', closeDrawer);
  });

  if (drawer) {
    const backdrop = drawer.querySelector('.mobile-drawer__backdrop');
    if (backdrop) backdrop.addEventListener('click', closeDrawer);
  }

  /* ----------------------------------------------------------
   * Global Escape key handler
   * -------------------------------------------------------- */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (searchOverlay && searchOverlay.classList.contains('is-open')) {
      closeSearch();
    }
    if (drawer && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
    if (countryDropdown && countryDropdown.classList.contains('is-open')) {
      closeCountry();
    }
  });
})();
